<template>
  <div>
    <div 
      v-if="!items.length" 
      class="fg-card box empty"
    >
      <div class="box-body text-muted">
        No FAQ or guidelines found.
      </div>
    </div>

    <div
      v-for="item in items"
      :key="item.id"
      class="fg-card box"
    >
      <div
        :class="!thumbnailUrl(item) ? badge(item).cls : ''"
        class="fg-thumb"
      >
        <img
          v-if="thumbnailUrl(item)"
          :src="thumbnailUrl(item)"
          alt="thumbnail"
        >
        <span v-else>{{ badge(item).label }}</span>
      </div>

      <div class="fg-body">
        <div class="fg-title-row">
          <h6 class="fg-title">{{ item.title }}</h6>
          <faq-guideline-new-badge v-if="item.is_new" />
          <span
            v-if="canManage && item.is_scheduled"
            class="fg-status fg-status--scheduled"
          >Scheduled publish on {{ scheduledDate(item) }}</span>
          <span
            v-else-if="canManage"
            :class="statusClass(item.status)"
            class="fg-status"
          >{{ item.status }}</span>
        </div>
        <p class="fg-desc">{{ item.description || 'No description' }}</p>
      </div>

      <div class="fg-actions">
        <!-- Inline buttons (wide screens) -->
        <div class="fg-actions-inline">
          <button
            type="button"
            class="btn white"
            title="View detail"
            @click="$emit('detail', item)"
          >
            <i class="fa fa-eye" />
          </button>
          <template v-if="canManage">
            <button
              type="button"
              class="btn white"
              title="Edit"
              @click="$emit('edit', item)"
            >
              <i class="fa fa-pencil" />
            </button>
            <button
              v-if="item.status !== 'archived'"
              type="button"
              class="btn white fg-icon-btn--archive"
              title="Archive"
              @click="$emit('archive', item)"
            >
              <i class="fa fa-archive" />
            </button>
            <button
              type="button"
              class="btn white fg-icon-btn--delete"
              title="Delete"
              @click="$emit('delete', item)"
            >
              <i class="fa fa-trash" />
            </button>
          </template>
        </div>

        <!-- Kebab menu (small screens) -->
        <div class="fg-actions-menu dropdown">
          <button
            type="button"
            class="btn white btn-sm"
            data-toggle="dropdown"
            aria-expanded="false"
          >
            <i class="fa fa-ellipsis-v" />
          </button>
          <div class="dropdown-menu dropdown-menu-right">
            <button
              type="button"
              class="dropdown-item"
              @click="$emit('detail', item)"
            >
              Detail
            </button>
            <template v-if="canManage">
              <button
                type="button"
                class="dropdown-item"
                @click="$emit('edit', item)"
              >
                Edit
              </button>
              <button
                v-if="item.status !== 'archived'"
                type="button"
                class="dropdown-item"
                @click="$emit('archive', item)"
              >
                Archive
              </button>
              <button
                type="button"
                class="dropdown-item text-danger"
                @click="$emit('delete', item)"
              >
                Delete
              </button>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import FaqGuidelineNewBadge from './faq-guideline-new-badge.vue';

const FILE_BADGES = {
    pdf: { label: 'PDF', cls: 'is-pdf' },
    ppt: { label: 'PPT', cls: 'is-ppt' },
    pptx: { label: 'PPTX', cls: 'is-ppt' },
    mp4: { label: 'MP4', cls: 'is-video' },
    mov: { label: 'MOV', cls: 'is-video' },
};

export default {
    components: { FaqGuidelineNewBadge },

    props: {
        items: {
            type: Array,
            default: () => [],
        },
        canManage: {
            type: Boolean,
            default: false,
        },
    },

    methods: {
        thumbnailUrl(item) {
            const media = (item.media_urls || []).find(
                m => m.collection_name === 'faq_guideline_thumbnail',
            );
            return media ? media.s512 || media.original : null;
        },

        // Derive a colored file-type badge from the first attachment, falling
        // back to a type label when there are no files.
        badge(item) {
            const file = (item.media_urls || []).find(
                m => m.collection_name === 'faq_guideline_files',
            );

            if (file) {
                const ext = (file.original || '')
                    .split('.')
                    .pop()
                    .toLowerCase();
                if (FILE_BADGES[ext]) {
                    return FILE_BADGES[ext];
                }
            }

            return item.type === 'guideline'
                ? { label: 'GUIDE', cls: 'is-doc' }
                : { label: 'FAQ', cls: 'is-faq' };
        },

        statusClass(status) {
            return `fg-status--${status}`;
        },

        scheduledDate(item) {
            return moment(item.published_at).format('DD MMM YYYY, HH:mm');
        },
    },
};
</script>

<style scoped>
.fg-card {
    display: flex;
    align-items: center;
    background: #fff;
    border: 1px solid #edf0f2;
    padding: 18px 20px;
    margin-bottom: 12px;
    transition: box-shadow 0.15s ease;
}

.fg-card.empty {
    justify-content: center;
}

.fg-card:last-child {
    margin-bottom: 0;
}
.fg-card:hover {
    box-shadow: 0 2px 10px rgba(45, 60, 80, 0.06);
}

.fg-thumb {
    flex: 0 0 56px;
    width: 56px;
    height: 56px;
    margin-right: 18px;
    border-radius: 10px;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.4px;
    color: #fff;
    background: #adb5bd;
}
.fg-thumb img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}
.fg-thumb.is-pdf {
    background: #e8554e;
}
.fg-thumb.is-ppt {
    background: #ec894d;
}
.fg-thumb.is-video {
    background: #4c8bf5;
}
.fg-thumb.is-doc,
.fg-thumb.is-faq {
    background: #2a9d8f;
}

.fg-body {
    flex: 1 1 auto;
    min-width: 0;
}
.fg-title-row {
    display: flex;
    align-items: center;
    gap: 8px;
}
.fg-title {
    margin: 0;
    font-size: 15px;
    font-weight: 700;
    color: #2b3543;
}

.fg-status {
    display: inline-flex;
    align-items: center;
    height: 20px;
    padding: 0 10px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
    line-height: 1;
    text-transform: capitalize;
}
.fg-status--draft {
    background: #eef1f4;
    color: #6b7785;
}
.fg-status--published {
    background: #e6f6f3;
    color: #2a9d8f;
}
.fg-status--archived {
    background: #fbeae6;
    color: #d9663f;
}
.fg-status--scheduled {
    background: #fef3e6;
    color: #c77700;
}
.fg-desc {
    margin: 4px 0 0;
    font-size: 13px;
    line-height: 1.5;
    color: #8a94a6;
    display: -webkit-box;
    line-clamp: 2;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.fg-actions {
    flex: 0 0 auto;
    margin-left: 18px;
}
.fg-actions-inline {
    display: flex;
    gap: 8px;
}
.fg-actions-inline .btn {
    min-width: 0;
    width: 38px;
    height: 38px;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    line-height: 1;
    color: #647285;
}
.fg-actions-inline .fg-icon-btn--archive {
    color: #2a9d8f;
}
.fg-actions-inline .fg-icon-btn--archive:hover {
    background: #e6f6f3;
    color: #2a9d8f;
}
.fg-actions-inline .fg-icon-btn--delete {
    color: #e8554e;
}
.fg-actions-inline .fg-icon-btn--delete:hover {
    background: #fbeae6;
    color: #e8554e;
}
.fg-actions-menu {
    display: none;
}
@media (max-width: 767.98px) {
    .fg-actions-inline {
        display: none;
    }
    .fg-actions-menu {
        display: inline-block;
    }
}
</style>



// WEBPACK FOOTER //
// resources/assets/js/franchise_component/faq_guideline/faq-guideline-list.vue